import React, { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import NumberFormat from 'react-number-format';
import styles from './styles.module.scss';

import { getCart } from '../../../redux/actions/cartActions';
import ApiCart from '../../../api/Cart/CartApiFL';

import Images from '../Images';
import Description from '../Description';
import Rating from '../Rating';
import Favorite from '../Favorite';
import Share from '../Share';
import Quantity from '../Quantity';
import Reviews from '../Reviews';

import Config from '../../../config/config';

const ShareIcons = ({ content, spanish, cmsData }) => (
  <div className={styles.icons}>
    <Favorite content={content} spanish={spanish} />
    <Share cmsData={cmsData} />
  </div>
);

const Scrollers = ({
  scrollToDescription,
  scrollToUsage,
  scrollToIngredients,
  spanish,
  content,
  cmsData,
}) => (
  <div className={styles.scrollers}>
    <button type="button" onClick={scrollToDescription}>
      {spanish ? content?.ReadMoreSpanish : content?.ReadMoreEnglish}
    </button>
    {cmsData && cmsData?.Usage && <span>|</span>}
    {cmsData && cmsData?.Usage && (
      <button type="button" onClick={scrollToUsage}>
        {spanish ? content?.UsageSpanish : content?.UsageEnglish}
      </button>
    )}
    {cmsData && cmsData?.Ingredients && <span>|</span>}
    {cmsData && cmsData?.Ingredients && (
      <button type="button" onClick={scrollToIngredients}>
        {spanish ? content?.IngredientsSpanish : content?.IngredientsEnglish}
      </button>
    )}
    {cmsData && cmsData?.PDF && cmsData?.PDF?.url && <span>|</span>}
    {cmsData && cmsData?.PDF && cmsData?.PDF?.url && (
      <a
        href={Config.cdn.cdn_path + cmsData?.PDF?.url}
        target="_blank"
        rel="noreferrer noopener"
      >
        {spanish ? content?.DownloadSpanish : content?.DownloadEnglish}
      </a>
    )}
  </div>
);

const Stats = ({ rating, reviews, onClick, content, spanish }) => (
  <div className={styles.stats}>
    <Rating value={rating} />
    <div className={styles.reviews}>
      <button type="button" onClick={onClick}>{`(${reviews}) ${
        spanish ? 'Revisiones' : 'Reviews'
      }`}</button>
      <button type="button" onClick={onClick}>
        {spanish ? content?.ShareCommentSpanish : content?.ShareCommentEnglish}
      </button>
    </div>
  </div>
);

const AddTocart = ({
  content,
  spanish,
  selectedQuantity,
  onChangeQuantiy,
  onAdd,
  onRemove,
  onSubmitToCart,
  cmsData,
}) => (
  <div className={styles.addtocart}>
    <div className={styles.combo}>
      <Quantity
        number={selectedQuantity}
        onChange={onChangeQuantiy}
        onAdd={onAdd}
        onRemove={onRemove}
      />
      <div className={styles.btn}>
        <button
          type="button"
          className="btn btn-white"
          onClick={onSubmitToCart}
        >
          {spanish ? content?.AddToCartSpanish : content?.AddToCartEnglish}
        </button>
      </div>
    </div>
    <ShareIcons content={content} spanish={spanish} cmsData={cmsData} />
  </div>
);

const ProductData = ({
  productInfoApi,
  selectedQuantity,
  onChangeQuantiy,
  onAdd,
  onRemove,
  onSubmitToCart,
  scrollToDescription,
  scrollToUsage,
  scrollToIngredients,
  scrollToReviews,
  spanish,
  content,
  cmsData,
}) => (
  <section className={styles.section}>
    <header className={styles.header}>
      <h1>{cmsData?.Name}</h1>
      <p>{`${spanish ? 'Producto' : 'Product'} #${cmsData?.SKU}`}</p>
    </header>

    <Stats
      rating={productInfoApi?.Calificacion}
      reviews={productInfoApi?.Revisiones}
      onClick={scrollToReviews}
      spanish={spanish}
      content={content}
    />

    <div className={styles.description}>
      <p>{spanish ? cmsData?.IntroSpanish : cmsData?.IntroEnglish}</p>
    </div>

    <Scrollers
      scrollToDescription={scrollToDescription}
      scrollToUsage={scrollToUsage}
      scrollToIngredients={scrollToIngredients}
      spanish={spanish}
      content={content}
      cmsData={cmsData}
    />

    <div className={styles.specs}>
      {productInfoApi?.unidades_envase && productInfoApi?.nump_caja && (
        <p>{`${productInfoApi?.unidades_envase} ${productInfoApi?.presentacion}`}</p>
      )}
    </div>

    <div className={styles.price}>
      <p>
        <NumberFormat
          value={productInfoApi?.precioRetail}
          thousandSeparator
          prefix="$"
          suffix=" MXN"
          displayType="text"
        />
      </p>
    </div>

    {productInfoApi?.existencia === false ? (
      <div className={styles.soldOut}>
        <p>{spanish ? content?.SoldOutSpanish : content?.SoldOutEnglish}</p>
      </div>
    ) : (
      <AddTocart
        content={content}
        selectedQuantity={selectedQuantity}
        onAdd={onAdd}
        onRemove={onRemove}
        onChangeQuantiy={onChangeQuantiy}
        spanish={spanish}
        onSubmitToCart={onSubmitToCart}
        cmsData={cmsData}
      />
    )}
  </section>
);

const ProductInfo = ({ content, cmsData, login, spanish, productInfoApi }) => {
  const dispatch = useDispatch();
  const currentCart = useSelector(
    (globalState) => globalState.cartInfo.InfoCarrito
  );
  const currentOfficeId = useSelector(
    (globalState) => globalState.estadoSelection.selectedEstadoId
  );
  const refToDescription = useRef(null);
  const refToUsage = useRef(null);
  const refToIngredients = useRef(null);
  const refToReviews = useRef(null);
  const scrollToDescription = () => refToDescription.current.scrollIntoView();
  const scrollToUsage = () => refToUsage.current.scrollIntoView();
  const scrollToIngredients = () => refToIngredients.current.scrollIntoView();
  const scrollToReviews = () => refToReviews.current.scrollIntoView();
  // eslint-disable-next-line no-unused-vars
  const [selectedQuantity, setSelectedQuantity] = useState(1);

  const onAdd = () => {
    const addByOne = selectedQuantity + 1;
    setSelectedQuantity(addByOne);
  };

  const onRemove = () => {
    if (selectedQuantity >= 2) {
      const removeByOne = selectedQuantity - 1;
      setSelectedQuantity(removeByOne);
    }
  };

  const onChangeQuantiy = (e) => {
    setSelectedQuantity(e.target.value);
  };

  const onSubmitToCart = async () => {
    const currentCartDetail = currentCart[0]?.DetalleCarrito;
    const currentCartId = currentCart[0]?.idCarrito;
    const checkProd = currentCartDetail?.some(
      (el) => el.idProducto === productInfoApi.cve_prod
    );
    if (currentCartDetail && checkProd) {
      const producstArray = currentCartDetail.map((el) => ({
        Cve_Prod: el.idProducto,
        Cantidad:
          el.idProducto === productInfoApi.cve_prod
            ? el.Cantidad + selectedQuantity
            : el.Cantidad,
        TipoUnidad: el.TipoUnidad,
      }));

      if (producstArray.length) {
        const payloadUpdate = {
          Cliente: Config.api_services.client,
          Pais: 'MEX',
          Ambiente: Config.api_services.environment,
          AgregarCarrito: {
            IdCarrito: currentCartId || 0,
            IdEstado: currentOfficeId,
            TipoVenta: 1,
            FormaVenta: 3,
            IdFbo: 0,
            Productos: producstArray,
          },
        };

        const respUpdate = await ApiCart.updateCart(payloadUpdate);
        if (respUpdate.Codigo === 200) {
          dispatch(getCart(respUpdate.IdCarrito));
        }
      }
    } else {
      const payload = {
        Cliente: Config.api_services.client,
        Pais: 'MEX',
        Ambiente: Config.api_services.environment,
        AgregarCarrito: {
          IdCarrito: currentCartId || 0,
          IdEstado: currentOfficeId,
          TipoVenta: 1,
          FormaVenta: 3,
          IdFbo: 0,
          Productos: [
            {
              Cve_Prod: productInfoApi.cve_prod,
              Cantidad: selectedQuantity,
              TipoUnidad: 'PI',
            },
          ],
        },
      };

      const resp = await ApiCart.addToCart(payload);
      if (resp.Codigo === 200) {
        dispatch(getCart(resp.IdCarrito));
      }
    }
  };

  return (
    <div className={styles.container}>
      {content && cmsData && (
        <>
          <Images cmsData={cmsData} />
          <ProductData
            scrollToDescription={scrollToDescription}
            scrollToUsage={scrollToUsage}
            scrollToIngredients={scrollToIngredients}
            scrollToReviews={scrollToReviews}
            spanish={spanish}
            content={content}
            cmsData={cmsData}
            login={login}
            productInfoApi={productInfoApi}
            selectedQuantity={selectedQuantity}
            onAdd={onAdd}
            onRemove={onRemove}
            onChangeQuantiy={onChangeQuantiy}
            onSubmitToCart={onSubmitToCart}
          />
          <Description
            refToDescription={refToDescription}
            refToUsage={refToUsage}
            refToIngredients={refToIngredients}
            spanish={spanish}
            content={content}
            cmsData={cmsData}
          />
          <Reviews
            refToReviews={refToReviews}
            spanish={spanish}
            content={content}
            login={login}
            productInfoApi={productInfoApi}
          />
        </>
      )}
    </div>
  );
};

export default ProductInfo;
